import React from 'react';
import PropTypes from 'prop-types';
import { TrustpilotWidget } from '../USP/USP';
import defaultImageDesktop from '../../assets/school-shop/Studietur-Desktop.jpg';
import defaultImageMobile from '../../assets/school-shop/Studietur-Mobile.jpg';
import './SecondaryBanner.css';

const TITLE_SEPARATOR = ' / ';

/**
 * SecondaryBanner — photo banner with a glass tag, editorial title and
 * Trustpilot widget.
 *
 * Figma: Desktop `8746:73145` (1360×480), Mobile `8826:226851` (full-bleed, 480 tall).
 *
 * - Without a `tag` the whole block is not rendered.
 * - Without a `title` (e.g. a school shop with no school chosen) the tag copy
 *   is promoted to the title and the glass tag is hidden.
 * - `title` segments joined by " / " (e.g. "School / Class") render the
 *   separator in gray-2.
 * - `backgroundImage` is the desktop photo; `backgroundImageMobile` falls back
 *   to it, so a single image works for both breakpoints.
 */
const SecondaryBanner = ({
  tag,
  title = '',
  backgroundImage = defaultImageDesktop,
  backgroundImageMobile,
  trustpilot = {
    businessUnitId: '46905da900006400050004f8',
    locale: 'da-DK',
    reviewUrl: 'https://dk.trustpilot.com/review/cls.dk',
  },
}) => {
  if (!tag || !String(tag).trim()) return null;

  const mobileImage = backgroundImageMobile
    ?? (backgroundImage === defaultImageDesktop ? defaultImageMobile : backgroundImage);
  const hasTitle = Boolean(title && String(title).trim());
  const titleParts = String(hasTitle ? title : tag).split(TITLE_SEPARATOR);

  return (
    <section className="secondary-banner">
      <div className="secondary-banner__container">
        <picture className="secondary-banner__media" aria-hidden="true">
          <source media="(min-width: 1024px)" srcSet={backgroundImage} />
          <img className="secondary-banner__image" src={mobileImage} alt="" />
        </picture>

        <div className="secondary-banner__content">
          <div className="secondary-banner__heading">
            {hasTitle && (
              <div className="secondary-banner__tag">
                <p className="typo-h4 secondary-banner__tag-text">{tag}</p>
              </div>
            )}
            <h1 className="typo-h1-editorial secondary-banner__title">
              {titleParts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && (
                    <span className="secondary-banner__title-sep">{TITLE_SEPARATOR}</span>
                  )}
                  {part}
                </React.Fragment>
              ))}
            </h1>
          </div>

          {trustpilot && (
            <div className="secondary-banner__trustpilot">
              <TrustpilotWidget {...trustpilot} theme="dark" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

SecondaryBanner.propTypes = {
  tag: PropTypes.node,
  title: PropTypes.string,
  backgroundImage: PropTypes.string,
  backgroundImageMobile: PropTypes.string,
  trustpilot: PropTypes.shape({
    businessUnitId: PropTypes.string.isRequired,
    locale: PropTypes.string,
    reviewUrl: PropTypes.string,
  }),
};

export default SecondaryBanner;
